import axios from "axios";

const RAW = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
// Use relative URL when the configured backend host matches the current page host,
// so cookies are first-party and never blocked as cross-site credentials.
function _computeBase() {
  if (!RAW) return "";
  if (typeof window === "undefined") return RAW;
  try {
    const u = new URL(RAW);
    return u.host === window.location.host ? "" : RAW;
  } catch {
    return RAW;
  }
}
const BASE = _computeBase();
export const API = `${BASE}/api`;
export const ASSET_BASE = BASE; // for <img src=...> on /api/files/...

const client = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Dedicated axios instance for the refresh call so it never triggers
// the interceptor and causes a recursion.
const refreshClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Single-flight: dedupe parallel refresh attempts.
let refreshPromise = null;

// On 401 → try one silent refresh, retry the original request, else /login.
client.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    const url = original?.url || "";
    const status = err.response?.status;

    // Never try to refresh for auth-adjacent endpoints or the refresh call itself
    const isAuthEndpoint =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/me") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");

    if (status !== 401 || !original || original._retry || isAuthEndpoint) {
      return Promise.reject(err);
    }

    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = refreshClient
          .post("/auth/refresh", {})
          .finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      // Retry the original request with new cookie
      return client(original);
    } catch (refreshErr) {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
      return Promise.reject(refreshErr);
    }
  }
);

/**
 * Turn ANY error into a user-friendly string.
 *
 * Accepts either:
 *   - the raw ``detail`` value returned by FastAPI (string, list of
 *     validation errors, plain object), OR
 *   - an Axios error object (extracts ``response.data.detail`` first,
 *     then falls back to ``response.statusText`` / ``err.message``).
 *
 * A ``fallback`` second argument is returned when nothing usable is
 * present, so callers can always show a sensible message.
 */
export function formatApiErrorDetail(source, fallback = "Something went wrong. Please try again.") {
  if (source == null) return fallback;

  // Axios error object?  Prefer the server-supplied ``detail`` field.
  if (source && typeof source === "object" && source.isAxiosError) {
    const data   = source.response?.data;
    const detail = data && typeof data === "object" ? data.detail : data;
    if (detail != null) return formatApiErrorDetail(detail, fallback);
    if (source.response?.statusText) return `${source.response.status}: ${source.response.statusText}`;
    if (source.message) return source.message;
    return fallback;
  }

  if (typeof source === "string") return source || fallback;
  if (Array.isArray(source)) {
    const s = source
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
    return s || fallback;
  }
  if (source && typeof source === "object") {
    if (typeof source.msg === "string") return source.msg;
    if (typeof source.detail === "string") return source.detail;
  }
  const s = String(source);
  return s === "[object Object]" ? fallback : s;
}

export default client;
